import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { User, Eye, Edit2, Loader2 } from 'lucide-react';
import { useTheme } from 'next-themes';
import Button from './ui/Button';
import Input from './ui/Input';
import Tooltip from './ui/Tooltip';
import BasicCodeBlock from './BasicCodeBlock';
import LimitReachedDialog from './LimitReachedDialog';
import CustomTable, { CustomThead, CustomTh, CustomTr, CustomTd } from './CustomTable';
import CustomCheckbox from './CustomCheckbox';
import FootnoteBackButton from './FootnoteBackButton';
import { useSubscription } from '../lib/hooks/useSubscription';
import { useAuthStore } from '../store/auth-store';
import { DEFAULT_TAGS } from '../lib/constants';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import type { Note, Tag } from '../types';
import TagModal from './TagModal';
import TagContextMenu from './TagContextMenu';
import DisabledFeatureDialog from './DisabledFeatureDialog';

interface NoteEditorProps {
  note?: Note;
  availableTags: Tag[];
  onSave: (note: Partial<Note>) => Promise<boolean>;
  onCancel: () => void;
}

const TITLE_MAX_LENGTH = 75;
const TAG_NAME_MAX_LENGTH = 25;

const PLACEHOLDER_CONTENT = `# Markdown Examples

## Basic Syntax

### Text Formatting
**Bold text** or __another bold__
*Italic text* or _another italic_
~~Strikethrough text~~
**Bold and _nested italic_**
***All bold and italic***

### Lists
1. First ordered item
2. Second ordered item
   - Unordered sub-list
   - Another item
3. Third ordered item

- Unordered list
  1. Ordered sub-list
  2. Another item
- Another item

### Links and Images
[GitHub](https://github.com)
![Alt text](https://example.com/image.jpg)

### Blockquotes
> This is a blockquote
> Multiple lines
>> Nested blockquote

### Tables
| Header 1 | Header 2 |
|----------|----------|
| Cell 1    | Cell 2    |
| Cell 3    | Cell 4    |

### Task Lists
- [x] Completed task
- [ ] Incomplete task
- [ ] Another task

## Code Examples

Inline \`code\` has \`back-ticks around\` it.

\`\`\`javascript
// JavaScript example
function hello(name) {
  console.log(\`Hello, \${name}!\`);
}

hello('World');
\`\`\`

\`\`\`python
# Python example
def fibonacci(n):
    if n <= 1:
        return n
    return fibonacci(n-1) + fibonacci(n-2)

print(fibonacci(10))
\`\`\`

### HTML Support
<details>
<summary>Click to expand!</summary>
This content is hidden by default.
</details>

<div align="center">
  <h3>Centered content</h3>
  <p>This text is centered using HTML.</p>
</div>

### Footnotes
Here's a sentence with a footnote[^1].

[^1]: This is the footnote.

### Syntax Highlighting
\`\`\`typescript
interface User {
  id: string;
  name: string;
  email: string;
}

function greetUser(user: User): string {
  return \`Hello, \${user.name}!\`;
}
\`\`\`

### Keyboard Keys
Press <kbd>Ctrl</kbd> + <kbd>C</kbd> to copy.`;

const NoteEditor: React.FC<NoteEditorProps> = ({
  note,
  onSave,
  onCancel,
  availableTags
}) => {
  const { theme } = useTheme();
  const { user } = useAuthStore();
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [selectedTags, setSelectedTags] = useState<string[]>(note?.tags || []);
  const [isPreview, setIsPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [showDisabledDialog, setShowDisabledDialog] = useState(false);
  const [limitType, setLimitType] = useState<'storage' | 'characters'>('characters');
  const { limits, checkLimit, storageUsed, subscription } = useSubscription();
  const [customTag, setCustomTag] = useState('');
  const [customTagColor, setCustomTagColor] = useState('#4f46e5');
  const [showTagModal, setShowTagModal] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    tag: Tag;
    position: { x: number; y: number };
  } | null>(null);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [tags, setTags] = useState<Tag[]>([...DEFAULT_TAGS]);

  useEffect(() => {
    if (!user) return;
    
    const loadTags = async () => {
      try {
        const { data: customTags, error } = await supabase
          .from('tags')
          .select('*')
          .eq('user_id', user.id);

        if (error) throw error;

        //Adding DEFAULT_TAGS as fallback
        const allTags = [...DEFAULT_TAGS, ...(customTags || [])];
        setTags(allTags);
      } catch (error) {
        console.error('Error loading tags:', error);
        toast.error('Failed to load custom tags');
        //Setting default tags to be able to continue and the website not to be affected
        setTags(DEFAULT_TAGS);
      }
    };

    loadTags();
  }, [user]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value.slice(0, TITLE_MAX_LENGTH);
    setTitle(newTitle);
  };

  const handleContentChange = (newContent: string) => {
    const contentLength = new Blob([newContent]).size;
    const characterCount = newContent.length;
    const totalStorageAfterChange = storageUsed + (contentLength - new Blob([content]).size);

    if (!checkLimit('maxStorage', totalStorageAfterChange)) {
      setLimitType('storage');
      setShowLimitDialog(true);
      return;
    }

    if (!checkLimit('maxCharacters', characterCount)) {
      setLimitType('characters');
      setShowLimitDialog(true);
      return;
    }

    setContent(newContent);
  };

  const handleAddCustomTag = async (newTag?: { name: string; color: string; id?: string }) => {
    if (!user) {
      toast.error('You must be logged in to create tags');
      return;
    }

    if (subscription === 'free') {
      toast.error('Upgrade to Pro or Premium to create custom tags');
      return;
    }

    let tagName = customTag;
    let tagColor = customTagColor;

    if (newTag) {
      tagName = newTag.name;
      tagColor = newTag.color;
    }

    if (!tagName.trim()) {
      toast.error('Tag name is required');
      return;
    }

    if (tagName.length > TAG_NAME_MAX_LENGTH) {
      toast.error(`Tag name must be ${TAG_NAME_MAX_LENGTH} characters or less`);
      return;
    }

    // Check if tag already exists
    const existingTag = tags.find(
      tag => tag.name.toLowerCase() === tagName.toLowerCase()
    );

    if (existingTag) {
      toast.error('A tag with this name already exists');
      return;
    }

    try {
      let upsertData = {
        id: tagName.toLowerCase().replace(/[^a-z0-9]/g, '-'),
        name: tagName.trim(),
        color: tagColor,
        user_id: user.id
      };

      let query = supabase.from('tags');
      let operation;

      if (editingTag) {
        operation = query.update(upsertData)
          .eq('id', editingTag.id);
      } else {
        operation = query.insert([upsertData]);
      }

      const { data, error } = await operation.select().single();

      if (error) throw error;

      const updatedTags = [...tags.filter(tag => tag.id !== data.id), data];
      setTags(updatedTags);

      if (editingTag) {
        toast.success('Tag updated!');
      } else {
        toast.success('Custom tag created');
      }
      setEditingTag(null);
      setCustomTag('');
      setShowTagModal(false);

    } catch (error: any) {
      console.error('Error creating tag:', error);
      toast.error('Failed to create custom tag');
    }
  };

  const handleDeleteTag = async (tag: Tag) => {
    if (!user) {
      toast.error('You must be logged in.');
      return;
    }

    try {
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', tag.id)
        .eq('user_id', user.id);

      if (error) throw error;

      const updatedTags = tags.filter(t => t.id !== tag.id);
      setTags(updatedTags);
      setSelectedTags(prev => prev.filter(t => t !== tag.id));
      setContextMenu(null);
      toast.success('Tag deleted.');
    } catch (error: any) {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast.error('You must be logged in to save notes');
      return;
    }
    
    setIsLoading(true);
    try {
      const success = await onSave({
        title,
        content,
        tags: selectedTags
      });
      
      if (success) {
        toast.success(note ? 'Note updated successfully' : 'Note created successfully');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save note');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagClick = () => {
    setShowDisabledDialog(true);
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex justify-between items-center mb-1">
          <label htmlFor="title" className="block text-sm font-medium">
            Title
          </label>
          <span className="text-xs text-muted-foreground">
            {title.length}/{TITLE_MAX_LENGTH}
          </span>
        </div>
        <Input
          id="title"
          value={title}
          onChange={handleTitleChange}
          placeholder="Note title"
          maxLength={TITLE_MAX_LENGTH}
          required
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-1">
          <label htmlFor="content" className="block text-sm font-medium">
            Content
          </label>
          <Tooltip content={isPreview ? 'Edit' : 'Preview'} side="left">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsPreview(!isPreview)}
              className="h-8 w-8 p-0"
            >
              {isPreview ? (
                <Edit2 className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </Tooltip>
        </div>
        
        {isPreview ? (
          <div className="prose prose-sm dark:prose-invert max-w-none p-6 border rounded-lg bg-card overflow-y-auto max-h-[600px]">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                code({ node, inline, className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  if (!inline && match) {
                    return (
                      <BasicCodeBlock className={className}>
                        {String(children).replace(/\n$/, '')}
                      </BasicCodeBlock>
                    );
                  }
                  return (
                    <code 
                      className="px-1.5 py-0.5 rounded-md bg-black text-gray-300 font-mono text-sm"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                table: CustomTable,
                thead: CustomThead,
                th: CustomTh,
                tr: CustomTr,
                td: CustomTd,
                input({ type, checked }) {
                  if (type === 'checkbox') {
                    return <CustomCheckbox state={checked ? 'checked' : 'unchecked'} />;
                  }
                  return null;
                },
                h1: ({ children }) => (
                  <h1 className="scroll-m-20 text-4xl font-bold tracking-tight">
                    {children}
                  </h1>
                ),
                h2: ({ children }) => (
                  <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {children}
                  </h3>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="mt-6 border-l-4 border-primary pl-6 italic">
                    {children}
                  </blockquote>
                ),
                ul: ({ children }) => (
                  <ul className="my-6 ml-6 list-disc marker:text-primary">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="my-6 ml-6 list-decimal marker:text-primary">
                    {children}
                  </ol>
                ),
                hr: () => (
                  <hr className="my-6 border-muted" />
                ),
                a: ({ children, href }) => (
                  <a 
                    href={href}
                    className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {children}
                  </a>
                ),
                img: ({ src, alt }) => (
                  <img 
                    src={src} 
                    alt={alt} 
                    className="rounded-lg border shadow-sm"
                  />
                ),
                sup: ({ children }) => {
                  if (String(children).startsWith('^')) {
                    return (
                      <sup className="text-primary">
                        {children}
                        <FootnoteBackButton />
                      </sup>
                    );
                  }
                  return <sup>{children}</sup>;
                }
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        ) : (
          <textarea
            id="content"
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            className="w-full h-[600px] rounded-lg border bg-card px-4 py-3 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring dark:text-white bg-transparent dark:bg- scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent"
            placeholder={content ? content : PLACEHOLDER_CONTENT}
            required
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Tags ({selectedTags.length}/4)</label>
        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
          {DEFAULT_TAGS.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={handleTagClick}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 hover:ring-1 hover:ring-primary/50`}
              style={{ 
                backgroundColor: `${tag.color}20`,
                color: tag.color
              }}
            >
              {tag.name}
            </button>
          ))}
          {subscription !== 'free' && (
            <button
              type="button"
              onClick={handleTagClick}
              className="px-3 py-1 rounded-full text-sm font-medium bg-muted hover:bg-muted/80 transition-colors"
            >
              + New Tag
            </button>
          )}
        </div>
      </div>

      <TagModal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        onSave={handleAddCustomTag}
        editTag={editingTag}
        setEditingTag={setEditingTag}
      />

      {contextMenu && (
        <TagContextMenu
          tag={contextMenu.tag}
          position={contextMenu.position}
          onClose={() => setContextMenu(null)}
          onEdit={(tag) => {
            setContextMenu(null);
            setEditingTag(tag);
            setShowTagModal(true);
          }}
          onDelete={handleDeleteTag}
        />
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Save
        </Button>
      </div>

      <LimitReachedDialog
        isOpen={showLimitDialog}
        onClose={() => setShowLimitDialog(false)}
        type={limitType}
        limit={{
          current: limitType === 'storage' 
            ? new Blob([content]).size 
            : content.length,
          max: limitType === 'storage'
            ? limits.maxStorage
            : limits.maxCharacters
        }}
      />

      <DisabledFeatureDialog
        isOpen={showDisabledDialog}
        onClose={() => setShowDisabledDialog(false)}
      />
    </div>
  );
};

export default NoteEditor;
